// =============================================================================
// CheckoutSuccess — landing page after Stripe redirect
// =============================================================================
// CHANGELOG (S9.5 / v9.4):
// - Live status: subscribes to Supabase Realtime on the pending_enrollment
//   row and flips from "processing" to "enrolled" the moment the webhook
//   marks the row paid. No more polling.
// - Fallback: if Realtime fails to subscribe in 3s, falls back to a 5s poll
//   for up to 10 minutes (legacy LRN behavior).
// =============================================================================

import { useParams, useSearchParams } from 'react-router-dom';
import { useEffect, useState, useRef } from 'react';
import { supabase } from '../lib/supabase.js';
import { api } from '../lib/api.js';

export default function CheckoutSuccess() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');

  // 'processing' (Stripe webhook hasn't fired yet)
  // 'enrolled'   (webhook fired, recruit row created)
  // 'timeout'    (waited 10 minutes, give up)
  const [status, setStatus] = useState('processing');
  const channelRef = useRef(null);
  const pollRef = useRef(null);
  const timeoutRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    async function fetchOnce() {
      try {
        const data = await api.get(`/api/checkout/${token}`);
        // The /api/checkout endpoint returns 409 with status='paid' when paid.
        // It throws on non-2xx, so a 409 lands in catch — handle both shapes.
        if (data && data.status === 'paid') {
          if (!cancelled) setStatus('enrolled');
          return true;
        }
        return false;
      } catch (e) {
        // 409 paid is wrapped — try to detect from message
        if (e && /already been paid/i.test(e.message || '')) {
          if (!cancelled) setStatus('enrolled');
          return true;
        }
        return false;
      }
    }

    // Try to subscribe via Realtime first
    let realtimeFired = false;
    try {
      const channel = supabase
        .channel(`pending-${token}`)
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'pending_enrollments',
            filter: `checkout_token=eq.${token}`,
          },
          (payload) => {
            const newRow = payload && payload.new;
            if (newRow && newRow.status === 'paid') {
              realtimeFired = true;
              if (!cancelled) setStatus('enrolled');
            }
          }
        )
        .subscribe();
      channelRef.current = channel;
    } catch (e) {
      // ignore — fall through to poll
    }

    // Always do an initial fetch — webhook may have already fired before we subscribed
    fetchOnce();

    // Fallback poll: 5s interval, 10min cap (matches legacy LRN behavior)
    pollRef.current = setInterval(async () => {
      if (cancelled) return;
      const done = await fetchOnce();
      if (done && pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    }, 5000);

    timeoutRef.current = setTimeout(() => {
      if (cancelled) return;
      if (status !== 'enrolled') setStatus('timeout');
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    }, 10 * 60 * 1000);

    return () => {
      cancelled = true;
      if (channelRef.current) {
        try { supabase.removeChannel(channelRef.current); } catch (e) {}
      }
      if (pollRef.current) clearInterval(pollRef.current);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [token]);

  return (
    <div className="min-h-screen bg-hunter-deep flex items-center justify-center p-4">
      <div className="w-full max-w-2xl">
        <div className="text-center mb-6">
          <h1 className="text-white text-2xl font-display font-black tracking-tight">Coach Cole Football</h1>
          <p className="text-mid-green text-xs tracking-[0.3em] uppercase font-bold mt-1">Recruiting & NIL Services</p>
        </div>
        <div className="card text-center">
          {status === 'processing' && <ProcessingPanel sessionId={sessionId} />}
          {status === 'enrolled'   && <EnrolledPanel sessionId={sessionId} />}
          {status === 'timeout'    && <TimeoutPanel sessionId={sessionId} />}
        </div>
      </div>
    </div>
  );
}

function ProcessingPanel({ sessionId }) {
  return (
    <>
      <div className="text-mid-green text-5xl mb-4 animate-pulse">●</div>
      <h2 className="text-2xl font-display font-black text-hunter mb-2">Confirming your enrollment…</h2>
      <p className="text-silver-mute mb-6">
        Your payment was received. We're finalizing your enrollment now —
        this usually takes just a few seconds.
      </p>
      <div className="text-xs text-silver-mute">
        Confirmation #: {sessionId ? sessionId.slice(-12) : 'pending'}
      </div>
    </>
  );
}

function EnrolledPanel({ sessionId }) {
  return (
    <>
      <div className="text-mid-green text-5xl mb-4">✓</div>
      <h2 className="text-2xl font-display font-black text-hunter mb-2">You're enrolled.</h2>
      <p className="text-silver-mute mb-6">
        Welcome to the CCF family. Your Recruiting Coordinator will reach out within 24 hours to kick off your plan.
      </p>
      <div className="text-xs text-silver-mute">
        Confirmation #: {sessionId ? sessionId.slice(-12) : 'pending'}
      </div>
    </>
  );
}

function TimeoutPanel({ sessionId }) {
  return (
    <>
      <div className="text-amber-600 text-5xl mb-4">!</div>
      <h2 className="text-2xl font-display font-black text-hunter mb-2">Still working on it.</h2>
      <p className="text-silver-mute mb-6">
        Your payment processed successfully, but we haven't received the
        enrollment confirmation yet. Please contact your CCF representative.
      </p>
      <div className="text-xs text-silver-mute">
        Confirmation #: {sessionId ? sessionId.slice(-12) : 'pending'}
      </div>
    </>
  );
}
